import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Command } from "cmdk";
import { FolderKanban, Plus, Search, type LucideIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ANALYTICS_EVENTS } from "analytics";
import { Button } from "components/common/buttons/Button";
import {
  PROJECT_DEFAULT_SEGMENT,
  PROJECT_LINKS,
  PROJECT_NAV_SECTION_LABELS,
  PROJECT_STEPS,
  buildProjectHref,
  getProjectStepItemSegment,
} from "config/navigation";
import { useAnalytics } from "hooks/useAnalytics";
import { useCreateProjectLauncher } from "hooks/project";
import { useProjectsApi } from "hooks/useProjects";
import { useProjectIdentifiers } from "hooks/project/useProjectIdentifiers";
import { useUserCatalog } from "hooks/useSession";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace";

export interface PaletteGroup {
  label: string;
  items: Array<{ label: string; href: string; icon: LucideIcon }>;
}

/** Find anything's page list: the same steps, pages and labels as the sidebar. */
export function buildPaletteGroups(workspaceId: string, projectId: string): PaletteGroup[] {
  return [
    ...PROJECT_STEPS.map((step) => ({
      label: step.label,
      items: step.items.map((item) => ({
        label: item.label,
        href: buildProjectHref(workspaceId, projectId, getProjectStepItemSegment(step, item)),
        icon: item.icon,
      })),
    })),
    {
      label: PROJECT_NAV_SECTION_LABELS.links,
      items: PROJECT_LINKS.map((link) => ({
        label: link.label,
        href: buildProjectHref(workspaceId, projectId, link.segment),
        icon: link.icon,
      })),
    },
  ];
}

const isEditingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
};

/**
 * Runs before the palette opens and decides when it may: it receives the
 * opener and calls it once ready (e.g. after a modal sheet has closed).
 */
export type NavigationSearchOpenGate = (open: () => void) => void;

interface NavigationSearchContextValue {
  /** Opens Find anything, the same as Cmd/Ctrl+K. */
  openSearch: () => void;
  /**
   * Lets a surface that blocks the palette (the mobile sheet) hold every
   * open, from a trigger or the shortcut, until it is out of the way. Pass
   * null to remove the gate.
   */
  setOpenGate: (gate: NavigationSearchOpenGate | null) => void;
}

const NavigationSearchContext = createContext<NavigationSearchContextValue | null>(null);

/** Opens Find anything from anywhere inside the app shell. Null outside the shell. */
export const useNavigationSearch = (): NavigationSearchContextValue | null =>
  useContext(NavigationSearchContext);

/**
 * Find anything: the command palette and its Cmd/Ctrl+K shortcut. Mounted
 * once per app shell so the shortcut works even while the mobile sheet is
 * closed. Triggers (the header search field) open it through `useNavigationSearch`.
 */
export const GlobalNavigationSearchProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { workspaceId } = useWorkspaceIdentifiers();
  const { projectId } = useProjectIdentifiers();
  const { projects, isError: projectsLoadFailed } = useProjectsApi({
    take: 25,
    order: "DESC",
    workspaceId,
  });
  const { refetch: refetchCatalog } = useUserCatalog();
  const { launchCreateProject, createProjectFlowElement, canCreateWorkspaceProject } =
    useCreateProjectLauncher();

  const navigationGroups = useMemo(
    () => (workspaceId && projectId ? buildPaletteGroups(workspaceId, String(projectId)) : []),
    [projectId, workspaceId]
  );

  const trackOpened = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.NAVIGATION_SEARCH_OPENED, {
      workspace_id: workspaceId,
      project_id: projectId ? String(projectId) : undefined,
    });
  }, [projectId, trackEvent, workspaceId]);

  const openGateRef = useRef<NavigationSearchOpenGate | null>(null);
  const setOpenGate = useCallback((gate: NavigationSearchOpenGate | null) => {
    openGateRef.current = gate;
  }, []);

  const openNow = useCallback(() => {
    setOpen(true);
    trackOpened();
  }, [trackOpened]);

  /** Every open, from a trigger or the shortcut, goes through the gate. */
  const openSearch = useCallback(() => {
    const gate = openGateRef.current;
    if (gate) gate(openNow);
    else openNow();
  }, [openNow]);

  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing || typeof event.key !== "string") return;
      if (event.key.toLowerCase() !== "k" || (!event.metaKey && !event.ctrlKey)) return;
      if (isEditingTarget(event.target)) return;
      event.preventDefault();
      if (openRef.current) setOpen(false);
      else openSearch();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [openSearch]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open]);

  const selectResult = (destination: string, resultType: string, label: string) => {
    trackEvent(ANALYTICS_EVENTS.NAVIGATION_SEARCH_RESULT_SELECTED, {
      result_type: resultType,
      result_label: label,
      destination,
      workspace_id: workspaceId,
      project_id: projectId ? String(projectId) : undefined,
    });
    setOpen(false);
    navigate(destination);
  };

  const contextValue = useMemo(() => ({ openSearch, setOpenGate }), [openSearch, setOpenGate]);

  return (
    <NavigationSearchContext.Provider value={contextValue}>
      {children}

      {open && (
        <div
          role="presentation"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 px-4 pt-[12vh] backdrop-blur-[2px]"
        >
          <div
            role="dialog"
            aria-label="Find anything"
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl shadow-black/20"
          >
            <Command className="bg-card text-foreground" label="Find anything" loop shouldFilter>
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
                <Command.Input
                  autoFocus
                  placeholder="Where do you want to go?"
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
              </div>
              <Command.List className="max-h-[28rem] overflow-y-auto p-2">
                {/* A failed catalog is unknown, so no search result claims there is no project. */}
                <Command.Empty className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {projectsLoadFailed ? "No matching feature." : "No matching feature or project."}
                </Command.Empty>

                {projectsLoadFailed && (
                  <div
                    role="alert"
                    className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-muted-foreground"
                  >
                    <span>Couldn't load projects</span>
                    <Button
                      type="button"
                      variant="badge"
                      color="gray"
                      size="sm"
                      onClick={() => void refetchCatalog()}
                    >
                      Try again
                    </Button>
                  </div>
                )}

                {canCreateWorkspaceProject && (
                  <Command.Group heading="Actions" className="text-xs text-muted-foreground">
                    <Command.Item
                      value="new create project production import screenplay blank"
                      onSelect={() => {
                        setOpen(false);
                        launchCreateProject({ entrySource: "sidebar" });
                      }}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground aria-selected:bg-muted"
                    >
                      <Plus className="h-4 w-4 text-primary" aria-hidden />
                      New project
                    </Command.Item>
                  </Command.Group>
                )}

                {navigationGroups.map((group) => (
                  <Command.Group
                    key={group.label}
                    heading={group.label}
                    className="mt-2 text-xs text-muted-foreground"
                  >
                    {group.items.map((item) => (
                      <Command.Item
                        key={item.href}
                        value={`${group.label} ${item.label}`}
                        onSelect={() => selectResult(item.href, "feature", item.label)}
                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground aria-selected:bg-muted"
                      >
                        <item.icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                        {item.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
                ))}

                {workspaceId && projects.length > 0 && (
                  <Command.Group heading="Projects" className="mt-2 text-xs text-muted-foreground">
                    {projects.map((project) => {
                      const destination = buildProjectHref(
                        workspaceId,
                        String(project.projectId),
                        PROJECT_DEFAULT_SEGMENT
                      );
                      return (
                        <Command.Item
                          key={project.projectId}
                          value={`project production ${project.name}`}
                          onSelect={() => selectResult(destination, "project", project.name)}
                          className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground aria-selected:bg-muted"
                        >
                          <FolderKanban className="h-4 w-4 text-muted-foreground" aria-hidden />
                          <span className="truncate">{project.name}</span>
                          {project.isDemo ? (
                            <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                              Demo
                            </span>
                          ) : null}
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                )}
              </Command.List>
            </Command>
          </div>
        </div>
      )}
      {createProjectFlowElement}
    </NavigationSearchContext.Provider>
  );
};
